import { useNavigate } from "react-router-dom";
import { useDashboards } from "../context/DashboardsContext";
import * as api from "../api/client";

/**
 * Uses plain CSS grid rather than react-bootstrap's Row/Col: Bootstrap rows apply negative gutter
 * margins that assume a Bootstrap container's compensating padding. Inside our own padded page body
 * they pushed content wider than the viewport, which scrolled the whole shell sideways and clipped
 * the sidebar.
 */
export default function DashboardsPage() {
  const navigate = useNavigate();
  const { dashboards, removeDashboard, refresh } = useDashboards();

  // Start from nothing and build widgets with the builder (it opens straight away on the new dashboard).
  const onNew = async () => {
    const title = window.prompt("Name of the new dashboard", "New dashboard");
    if (title === null) return;
    try {
      const d = await api.createBlankDashboard(title.trim() || "New dashboard");
      await refresh();
      navigate(`/dashboards/${d.id}?build=1`);
    } catch (err) {
      window.alert("Could not create dashboard: " + err.message);
    }
  };

  const onDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this dashboard? This cannot be undone.")) return;
    try {
      await api.deleteDashboard(id);
      removeDashboard(id);
    } catch (err) {
      window.alert("Could not delete dashboard: " + err.message);
    }
  };

  const totalWidgets = dashboards.reduce((n, d) => n + d.widgetCount, 0);
  const totalCharts = dashboards.reduce((n, d) => n + (d.chartCount || 0), 0);
  const totalTables = dashboards.reduce((n, d) => n + (d.tableCount || 0), 0);
  const uniqueChats = new Set(dashboards.flatMap((d) => d.chatIds)).size;

  return (
    <main className="main">
      <div className="topbar">
        <div className="topbar-row">
          <div>
            <h1>Dashboards</h1>
            <p>Saved views built from your chats, or from scratch with the widget builder.</p>
          </div>
          <button className="dash-btn primary" onClick={onNew}>＋ New dashboard</button>
        </div>
      </div>

      <div className="page-body">
        {dashboards.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-mark">▩</div>
            <div className="empty-state-title">No dashboards yet</div>
            <div className="empty-state-sub">
              Select one or more chats in the sidebar, then choose <strong>Build</strong>.
            </div>
          </div>
        ) : (
          <>
            <div className="kpi-row">
              <div className="kpi">
                <div className="kpi-top">
                  <span className="kpi-label">DASHBOARDS</span>
                  <span className="kpi-icon">▩</span>
                </div>
                <div className="kpi-value">{dashboards.length}</div>
                <div className="kpi-hint">saved views</div>
              </div>
              <div className="kpi">
                <div className="kpi-top">
                  <span className="kpi-label">WIDGETS</span>
                  <span className="kpi-icon">▦</span>
                </div>
                <div className="kpi-value">{totalWidgets}</div>
                <div className="kpi-hint">across all dashboards</div>
              </div>
              <div className="kpi">
                <div className="kpi-top">
                  <span className="kpi-label">CHARTS / TABLES</span>
                  <span className="kpi-icon">▥</span>
                </div>
                <div className="kpi-value">
                  {totalCharts} <span className="kpi-divider">/</span> {totalTables}
                </div>
                <div className="kpi-hint">by widget type</div>
              </div>
              <div className="kpi">
                <div className="kpi-top">
                  <span className="kpi-label">SOURCE CHATS</span>
                  <span className="kpi-icon">▤</span>
                </div>
                <div className="kpi-value">{uniqueChats}</div>
                <div className="kpi-hint">conversations used</div>
              </div>
            </div>

            <div className="card-grid">
              {dashboards.map((d) => (
                <div key={d.id} className="dash-card" role="button" onClick={() => navigate(`/dashboards/${d.id}`)}>
                  <div className="dash-card-head">
                    <span className="dash-card-mark">▩</span>
                    {(d.sharedRoles || []).length > 0 && (
                      <span
                        className="dash-shared-badge"
                        title={d.isOwner === false ? `Shared with you by ${d.owner}` : `Shared with: ${d.sharedRoles.join(", ")}`}
                      >
                        G
                      </span>
                    )}
                    {d.isOwner !== false && (
                      <button
                        className="dash-card-del"
                        title="Delete dashboard"
                        onClick={(e) => onDelete(e, d.id)}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <div className="dash-card-title">{d.title}</div>
                  <div className="dash-card-meta">
                    <span className="pill neutral">
                      {d.widgetCount} widget{d.widgetCount === 1 ? "" : "s"}
                    </span>
                    <span className="pill neutral">
                      {d.chatIds.length} chat{d.chatIds.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="dash-card-foot">
                    {new Date(d.updatedAt * 1000).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
